/**
 * PL/SQL 실시간 점검 — 컴파일 전에 **화면 안에서** 구조만 본다 (2026-10-02, 사용자 결정 「화면 안에서 구조 점검」).
 *
 * 오라클 컴파일러를 대신하지 않는다. DB 없이 입력할 때마다 돌아야 하므로 「짝이 맞는가」 류만 본다:
 *  - BEGIN/END · IF/END IF · LOOP/END LOOP · CASE/END CASE 짝, 남는 END
 *  - 괄호·따옴표 짝
 *  - END 뒤 세미콜론
 *  - IF/ELSIF 뒤 THEN
 *  - 머리(PROCEDURE/FUNCTION/PACKAGE … IS|AS)와 닫는 이름(END 이름)이 같은가
 *  - 흔한 오타 예약어(ELSEIF, ENDIF, END LOOP 붙여쓰기 등)
 * 결과는 **코드 + 수치**다. 문구는 화면이 번역한다(editor:plsqlLint.*).
 *
 * 문자열·주석·"따옴표 식별자"·q'[...]' 안은 보지 않는다 — 그 안의 BEGIN 이 블록으로 세어지면 거짓 오류다.
 */

const TYPOS = {
    ELSEIF: 'ELSIF', ELSIFF: 'ELSIF', ENDIF: 'END IF', ENDLOOP: 'END LOOP', ENDCASE: 'END CASE',
    BEGN: 'BEGIN', BEIGN: 'BEGIN', BEGINE: 'BEGIN', EXCEPTON: 'EXCEPTION', EXEPTION: 'EXCEPTION', EXCPETION: 'EXCEPTION',
    RETRUN: 'RETURN', RETUN: 'RETURN', DECALRE: 'DECLARE', DECLRAE: 'DECLARE', THNE: 'THEN', TEHN: 'THEN',
    PROCEDUER: 'PROCEDURE', FUNCTOIN: 'FUNCTION', FUCNTION: 'FUNCTION',
};

/** 토큰 — { t: 'word'|'punct', v (대문자), line, col }. 문자열·주석은 버리되 닫히지 않은 것은 알린다 */
function tokenize(text, issues) {
    const out = [];
    let i = 0;
    let line = 1;
    let lineStart = 0;
    const n = text.length;
    const at = () => ({ line, col: i - lineStart + 1 });
    const nl = (k) => { line += 1; lineStart = k + 1; };
    while (i < n) {
        const ch = text[i];
        if (ch === '\n') { nl(i); i++; continue; }
        if (/\s/.test(ch)) { i++; continue; }
        // 주석
        if (ch === '-' && text[i + 1] === '-') { while (i < n && text[i] !== '\n') i++; continue; }
        if (ch === '/' && text[i + 1] === '*') {
            const p = at();
            i += 2;
            while (i < n && !(text[i] === '*' && text[i + 1] === '/')) { if (text[i] === '\n') nl(i); i++; }
            if (i >= n) { issues.push({ ...p, severity: 'error', code: 'UNCLOSED_COMMENT' }); break; }
            i += 2;
            continue;
        }
        // q'[ ... ]' 따옴표 리터럴
        if ((ch === 'q' || ch === 'Q') && text[i + 1] === "'" && text[i + 2]) {
            const p = at();
            const open = text[i + 2];
            const close = { '[': ']', '{': '}', '(': ')', '<': '>' }[open] || open;
            i += 3;
            while (i < n && !(text[i] === close && text[i + 1] === "'")) { if (text[i] === '\n') nl(i); i++; }
            if (i >= n) { issues.push({ ...p, severity: 'error', code: 'UNCLOSED_QUOTE' }); break; }
            i += 2;
            continue;
        }
        // 'string' ('' 은 따옴표 하나)
        if (ch === "'") {
            const p = at();
            i++;
            let closed = false;
            while (i < n) {
                if (text[i] === "'") { if (text[i + 1] === "'") { i += 2; continue; } closed = true; i++; break; }
                if (text[i] === '\n') nl(i);
                i++;
            }
            if (!closed) { issues.push({ ...p, severity: 'error', code: 'UNCLOSED_QUOTE' }); break; }
            out.push({ t: 'lit', v: "'", ...p });
            continue;
        }
        // "따옴표 식별자"
        if (ch === '"') {
            const p = at();
            i++;
            const s = i;
            while (i < n && text[i] !== '"' && text[i] !== '\n') i++;
            if (i >= n || text[i] !== '"') { issues.push({ ...p, severity: 'error', code: 'UNCLOSED_QUOTE' }); continue; }
            out.push({ t: 'word', v: text.slice(s, i), quoted: true, ...p });
            i++;
            continue;
        }
        if (/[A-Za-z_$#\u0080-￿]/.test(ch)) {
            const p = at();
            const s = i;
            while (i < n && /[A-Za-z0-9_$#\u0080-￿]/.test(text[i])) i++;
            out.push({ t: 'word', v: text.slice(s, i).toUpperCase(), ...p });
            continue;
        }
        const p = at();
        out.push({ t: 'punct', v: ch, ...p });
        i++;
    }
    return out;
}

const isWord = (tok, v) => tok && tok.t === 'word' && !tok.quoted && tok.v === v;

/**
 * @returns {Array<{ line, col, severity: 'error'|'warning', code, params? }>} 줄 순서
 */
export function lintPlsql(text) {
    const issues = [];
    if (!text || !text.trim()) return issues;
    const toks = tokenize(text, issues);
    const stack = [];            // { kind, line, col, name? }
    const parens = [];
    let pendingHeader = null;    // { kind:'SUB'|'PACKAGE', name, line, col } — IS/AS 를 기다린다
    let waitThen = null;         // IF/ELSIF 뒤 THEN 을 기다림

    for (let k = 0; k < toks.length; k++) {
        const tok = toks[k];
        const next = toks[k + 1];
        if (tok.t === 'punct') {
            if (tok.v === '(') parens.push(tok);
            else if (tok.v === ')') {
                if (parens.length === 0) issues.push({ line: tok.line, col: tok.col, severity: 'error', code: 'EXTRA_PAREN' });
                else parens.pop();
            } else if (tok.v === ';') {
                if (waitThen) {
                    issues.push({ line: waitThen.line, col: waitThen.col, severity: 'error', code: 'MISSING_THEN', params: { kw: waitThen.v } });
                    waitThen = null;
                }
                // 머리만 있고 IS/AS 없이 끝났다 — 패키지 명세의 선언(PROCEDURE p(...);) 이다
                pendingHeader = null;
            }
            continue;
        }
        if (tok.t !== 'word' || tok.quoted) continue;
        const v = tok.v;

        if (TYPOS[v]) issues.push({ line: tok.line, col: tok.col, severity: 'warning', code: 'TYPO', params: { word: v, hint: TYPOS[v] } });

        if (v === 'THEN') { waitThen = null; continue; }
        if (v === 'PROCEDURE' || v === 'FUNCTION') {
            pendingHeader = { kind: 'SUB', name: nameAt(toks, k + 1), line: tok.line, col: tok.col };
            continue;
        }
        if (v === 'PACKAGE') {
            pendingHeader = { kind: 'PACKAGE', name: nameAt(toks, isWord(next, 'BODY') ? k + 2 : k + 1), line: tok.line, col: tok.col };
            continue;
        }
        if ((v === 'IS' || v === 'AS') && pendingHeader) {
            // 머리의 이름이 스키마.이름 이면 마지막 조각
            stack.push({ ...pendingHeader });
            pendingHeader = null;
            continue;
        }
        if (v === 'DECLARE') { stack.push({ kind: 'DECLARE', line: tok.line, col: tok.col }); continue; }
        if (v === 'BEGIN') {
            const top = stack[stack.length - 1];
            // 선언부(DECLARE · 서브프로그램 · 패키지 초기화) 다음의 BEGIN 은 새 블록이 아니라 그 몸통이다
            if (top && (top.kind === 'DECLARE' || top.kind === 'SUB' || top.kind === 'PACKAGE') && !top.hasBody) {
                top.hasBody = true;
                continue;
            }
            stack.push({ kind: 'BEGIN', line: tok.line, col: tok.col, hasBody: true });
            continue;
        }
        if (v === 'IF' || v === 'ELSIF') {
            // END IF 의 IF 는 여기 오지 않는다(END 에서 먹는다)
            if (v === 'IF') stack.push({ kind: 'IF', line: tok.line, col: tok.col });
            waitThen = tok;
            continue;
        }
        if (v === 'LOOP') { stack.push({ kind: 'LOOP', line: tok.line, col: tok.col }); continue; }
        if (v === 'CASE') { stack.push({ kind: 'CASE', line: tok.line, col: tok.col }); continue; }
        if (v === 'END') {
            const kw = next && next.t === 'word' && !next.quoted && ['IF', 'LOOP', 'CASE'].includes(next.v) ? next.v : null;
            const top = stack[stack.length - 1];
            if (!top) {
                issues.push({ line: tok.line, col: tok.col, severity: 'error', code: 'EXTRA_END' });
            } else if (kw) {
                if (top.kind !== kw) {
                    issues.push({ line: tok.line, col: tok.col, severity: 'error', code: 'END_MISMATCH', params: { got: `END ${kw}`, want: endOf(top), line: top.line } });
                    // 맞는 것을 찾아 거기까지 닫는다 — 하나 틀렸다고 뒤가 전부 오류로 번지지 않게
                    const idx = findLast(stack, kw);
                    if (idx >= 0) stack.length = idx;
                } else {
                    stack.pop();
                }
                k++;   // IF/LOOP/CASE 를 먹었다
            } else if (top.kind === 'CASE') {
                stack.pop();   // CASE 식은 END 로 닫힌다
                continue;      // 식 안이라 세미콜론 검사는 하지 않는다
            } else if (top.kind === 'IF' || top.kind === 'LOOP') {
                issues.push({ line: tok.line, col: tok.col, severity: 'error', code: 'END_MISMATCH', params: { got: 'END', want: endOf(top), line: top.line } });
                const idx = stack.map(x => x.kind).lastIndexOf('BEGIN');
                stack.length = Math.max(0, idx);
            } else {
                const closed = stack.pop();
                // 닫는 이름 — END 이름; 의 이름이 머리와 다르면 경고(오라클은 오류다)
                const nameTok = next && next.t === 'word' && next.v !== 'IF' ? next : null;
                if (nameTok && closed.name && nameTok.v !== lastPart(closed.name)) {
                    issues.push({ line: nameTok.line, col: nameTok.col, severity: 'warning', code: 'END_NAME', params: { got: nameTok.v, want: lastPart(closed.name) } });
                }
                if ((closed.kind === 'DECLARE' || closed.kind === 'SUB') && !closed.hasBody) {
                    issues.push({ line: closed.line, col: closed.col, severity: 'error', code: 'MISSING_BEGIN' });
                }
            }
            // END … 다음은 세미콜론이어야 한다 (END IF; END LOOP 라벨; END 이름; END;)
            let j = k + 1;
            if (toks[j] && toks[j].t === 'word') j++;
            const after = toks[j];
            if (!after || !(after.t === 'punct' && after.v === ';')) {
                const ref = after || tok;
                issues.push({ line: ref.line, col: ref.col, severity: 'error', code: 'MISSING_SEMICOLON_END' });
            }
        }
    }

    for (const p of parens) issues.push({ line: p.line, col: p.col, severity: 'error', code: 'UNCLOSED_PAREN' });
    for (const b of stack) issues.push({ line: b.line, col: b.col, severity: 'error', code: 'MISSING_END', params: { want: endOf(b) } });
    if (waitThen) issues.push({ line: waitThen.line, col: waitThen.col, severity: 'error', code: 'MISSING_THEN', params: { kw: waitThen.v } });
    return issues.sort((a, b) => a.line - b.line || a.col - b.col);
}

function endOf(b) {
    if (b.kind === 'IF') return 'END IF';
    if (b.kind === 'LOOP') return 'END LOOP';
    if (b.kind === 'CASE') return 'END CASE';
    return 'END';
}
function findLast(stack, kind) {
    for (let i = stack.length - 1; i >= 0; i--) if (stack[i].kind === kind) return i;
    return -1;
}
/** 머리의 이름 — 스키마.이름 이면 이름 */
function nameAt(toks, k) {
    const a = toks[k];
    if (!a || a.t !== 'word') return null;
    const dot = toks[k + 1];
    if (dot && dot.t === 'punct' && dot.v === '.' && toks[k + 2] && toks[k + 2].t === 'word') return toks[k + 2].v;
    return a.v;
}
function lastPart(name) { const p = String(name || '').split('.'); return p[p.length - 1]; }
